import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield, BarChart3, Check, Info } from 'lucide-react';
import { useCookieConsent } from '../../context/CookieConsentContext';

export const CookieModal = () => {
  const { showModal, closeModal, saveCustomPreferences, consent } = useCookieConsent();
  const [statisticsEnabled, setStatisticsEnabled] = useState(false);

  // Initialize toggle state from current consent
  useEffect(() => {
    if (showModal) {
      setStatisticsEnabled(consent.preferences?.statistics ?? false);
    }
  }, [showModal, consent.preferences?.statistics]);

  const handleSave = () => {
    saveCustomPreferences({
      statistics: statisticsEnabled,
    });
  };

  return (
    <AnimatePresence>
      {showModal && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110]"
            onClick={closeModal}
            data-testid="cookie-modal-backdrop"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-[120] inset-x-4 top-4 bottom-20 sm:inset-6 sm:bottom-24 md:inset-0 md:flex md:items-center md:justify-center md:p-8"
            data-testid="cookie-modal"
          >
            <div className="h-full md:h-auto md:max-h-[80vh] md:w-full md:max-w-lg bg-[#1a2235] rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden flex flex-col">
              {/* Header */}
              <div className="relative p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
                <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald via-teal-400 to-blue-500" />
                
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald/20 to-teal-500/10 border border-emerald/20 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-emerald" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-lg sm:text-xl font-semibold text-white">
                        Cookie-Einstellungen
                      </h2>
                      <p className="text-xs sm:text-sm text-white/50 mt-0.5">
                        Verwalte deine Präferenzen
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                    data-testid="cookie-modal-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4 min-h-0">
                {/* Info box */}
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white/70 leading-relaxed">
                    Hier kannst du festlegen, welche Cookies du akzeptieren möchtest. 
                    Deine Auswahl wird gespeichert und du kannst sie jederzeit ändern.
                  </p>
                </div>
                
                {/* Necessary Cookies - Always on */}
                <div className="p-3 sm:p-5 bg-white/5 border border-white/10 rounded-xl">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white text-sm sm:text-base mb-1">
                          Notwendige Cookies
                        </h3>
                        <p className="text-xs sm:text-sm text-white/50 leading-relaxed">
                          Diese Cookies sind für die Grundfunktionen der Website erforderlich 
                          und können nicht deaktiviert werden.
                        </p>
                      </div>
                    </div>
                    
                    {/* Always on indicator */}
                    <div className="flex-shrink-0">
                      <div className="w-10 sm:w-12 h-6 sm:h-7 bg-emerald/20 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 bg-emerald rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                      </div>
                      <span className="text-[10px] sm:text-xs text-emerald mt-1 block text-center">Immer an</span>
                    </div>
                  </div>
                </div>
                
                {/* Statistics Cookies - Toggleable */}
                <div className="p-3 sm:p-5 bg-white/5 border border-white/10 rounded-xl">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white text-sm sm:text-base mb-1">
                          Statistik / Analyse
                        </h3>
                        <p className="text-xs sm:text-sm text-white/50 leading-relaxed">
                          Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
                        </p>
                      </div>
                    </div>
                    
                    {/* Toggle switch */}
                    <button
                      onClick={() => setStatisticsEnabled(!statisticsEnabled)}
                      className="flex-shrink-0"
                      data-testid="cookie-statistics-toggle"
                    >
                      <div className={`w-10 sm:w-12 h-6 sm:h-7 rounded-full transition-colors duration-200 flex items-center px-1 ${
                        statisticsEnabled ? 'bg-emerald/20' : 'bg-white/10'
                      }`}>
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full transition-all duration-200 flex items-center justify-center ${
                          statisticsEnabled 
                            ? 'translate-x-4 sm:translate-x-5 bg-emerald' 
                            : 'translate-x-0 bg-white/30'
                        }`}>
                          {statisticsEnabled && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-4 sm:p-6 border-t border-white/10 flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-xl font-medium text-sm transition-all duration-200"
                  data-testid="cookie-modal-cancel-btn"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-emerald hover:bg-emerald/90 text-white rounded-xl font-medium text-sm transition-all duration-200 shadow-lg shadow-emerald/20"
                  data-testid="cookie-modal-save-btn"
                >
                  Auswahl speichern
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieModal;
